import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../Common/Toast';
import { Modal } from '../Common/Modal';
import * as scheduleService from '../../services/scheduleService';

const SEGMENT_TYPES = [
  { value: 'content', label: 'Contenu' },
  { value: 'intro', label: 'Intro' },
  { value: 'outro', label: 'Outro' },
  { value: 'sponsor', label: 'Commanditaire' },
  { value: 'pause', label: 'Pause' },
  { value: 'credits', label: 'Générique' },
  { value: 'black', label: 'Black' },
];

function parseTimeToFrames(timeStr) {
  if (!timeStr) return 0;
  const parts = String(timeStr).split(':').map(Number);
  if (parts.length === 3) return parts[0] * 60 * 30 + parts[1] * 30 + parts[2];
  if (parts.length === 2) return parts[0] * 30 + parts[1];
  return 0;
}

function framesToTime(totalFrames) {
  if (!totalFrames || totalFrames <= 0) return '0:00:00';
  const m = Math.floor(totalFrames / (60 * 30));
  const s = Math.floor((totalFrames % (60 * 30)) / 30);
  const f = totalFrames % 30;
  return `${m}:${String(s).padStart(2, '0')}:${String(f).padStart(2, '0')}`;
}

function splitTime(timeStr) {
  if (!timeStr) return { mmss: '0:00', frames: 0 };
  const parts = String(timeStr).split(':').map(Number);
  if (parts.length === 3) {
    return { mmss: `${parts[0]}:${String(parts[1]).padStart(2, '0')}`, frames: parts[2] || 0 };
  }
  if (parts.length === 2) {
    return { mmss: `${parts[0]}:${String(parts[1]).padStart(2, '0')}`, frames: 0 };
  }
  return { mmss: '0:00', frames: 0 };
}

function joinTime(mmss, frames) {
  const f = Math.max(0, Math.min(29, parseInt(frames, 10) || 0));
  return `${mmss || '0:00'}:${String(f).padStart(2, '0')}`;
}

function computeStartTimes(segments) {
  const times = [];
  let acc = 0;
  for (let i = 0; i < segments.length; i++) {
    times.push(framesToTime(acc));
    acc += parseTimeToFrames(segments[i].duration);
  }
  return times;
}

function computeTotalDuration(segments) {
  let total = 0;
  for (const seg of segments) {
    total += parseTimeToFrames(seg.duration);
  }
  return framesToTime(total);
}

function hasEndSegment(segments) {
  return segments.some(
    (s) => s.segment_type === 'black' && s.description && /fin/i.test(s.description)
  );
}

export function EpisodeManager() {
  const { showId } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const { addToast } = useToast();
  const [show, setShow] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEpisode, setEditingEpisode] = useState(null);
  const isAdmin = hasRole('admin');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [showData, episodesData] = await Promise.all([
        scheduleService.getShow(showId),
        scheduleService.getEpisodes(showId),
      ]);
      setShow(showData);
      setEpisodes(Array.isArray(episodesData) ? episodesData : []);
    } catch (err) {
      addToast('Erreur lors du chargement', 'error');
    } finally {
      setLoading(false);
    }
  }, [showId, addToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = () => {
    setEditingEpisode(null);
    setModalOpen(true);
  };

  const handleEdit = async (episodeId) => {
    try {
      const full = await scheduleService.getEpisode(episodeId);
      setEditingEpisode(full);
      setModalOpen(true);
    } catch {
      addToast("Erreur lors du chargement de l'épisode", 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet épisode ?')) return;
    try {
      await scheduleService.deleteEpisode(id);
      addToast('Épisode supprimé', 'success');
      fetchData();
    } catch {
      addToast('Erreur lors de la suppression', 'error');
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingEpisode) {
        await scheduleService.updateEpisode(editingEpisode.id, data);
        addToast('Épisode mis à jour', 'success');
      } else {
        await scheduleService.createEpisode(showId, data);
        addToast('Épisode créé', 'success');
      }
      setModalOpen(false);
      fetchData();
    } catch (err) {
      addToast(err.response?.data?.message || 'Erreur', 'error');
      throw err;
    }
  };

  const handleDownloadXml = async (episodeId, episodeName) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://station.variammation.com/api'}/episodes/${episodeId}/xml`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );
      if (!response.ok) throw new Error('Failed to generate XML');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${show?.initials || 'EP'}_${episodeName || episodeId}.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      addToast('XML généré', 'success');
    } catch {
      addToast('Erreur lors de la génération XML', 'error');
    }
  };

  const grouped = {};
  episodes.forEach((ep) => {
    const s = ep.season_number || 1;
    if (!grouped[s]) grouped[s] = [];
    grouped[s].push(ep);
  });
  const seasons = Object.keys(grouped).map(Number).sort((a, b) => a - b);

  if (loading) return <div className="admin-page"><p>Chargement...</p></div>;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <Link to="/shows" className="breadcrumb-link">Émissions</Link>
          <span className="breadcrumb-sep">/</span>
          <h1 style={{ display: 'inline' }}>
            {show?.name || 'Émission'}
            <span className="show-initials-badge">{show?.initials}</span>
          </h1>
        </div>
        <button type="button" className="btn btn-primary" onClick={handleAdd}>
          + Nouvel épisode
        </button>
      </div>

      {show?.description && (
        <div className="series-description-box">
          <strong>Description de la série:</strong>
          <p>{show.description}</p>
        </div>
      )}

      {seasons.length === 0 ? (
        <p className="empty-state">Aucun épisode pour cette émission.</p>
      ) : (
        seasons.map((season) => (
          <div key={season} className="season-group">
            <h2 className="season-title">Saison {season}</h2>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Ép.</th>
                    <th>Titre</th>
                    <th>Animateur</th>
                    <th>Réalisateur</th>
                    <th>Année</th>
                    <th>Durée</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {grouped[season].map((ep) => (
                    <tr key={ep.id}>
                      <td className="ep-number">{ep.episode_number}</td>
                      <td>{ep.title || '—'}</td>
                      <td>{ep.animateur || '—'}</td>
                      <td>{ep.director || '—'}</td>
                      <td>{ep.production_year || '—'}</td>
                      <td>{ep.total_duration || '—'}</td>
                      <td className="actions-cell">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleEdit(ep.id)}
                        >
                          Modifier
                        </button>
                        {isAdmin && (
                          <button
                            type="button"
                            className="btn btn-sm btn-xml"
                            onClick={() => handleDownloadXml(ep.id, `S${season}E${ep.episode_number}`)}
                            title="Générer XML"
                          >
                            XML
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(ep.id)}
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingEpisode ? `Modifier S${editingEpisode.season_number}E${editingEpisode.episode_number}` : 'Nouvel épisode'}
        size="lg"
      >
        <EpisodeForm
          episode={editingEpisode}
          show={show}
          showId={showId}
          onSave={handleSave}
          onCancel={() => setModalOpen(false)}
        />
      </Modal>
    </div>
  );
}

function EpisodeForm({ episode, show, showId, onSave, onCancel }) {
  const isCreating = !episode;

  const [seasonNumber, setSeasonNumber] = useState(episode?.season_number ?? 1);
  const [episodeNumber, setEpisodeNumber] = useState(episode?.episode_number ?? 1);
  const [title, setTitle] = useState(episode?.title ?? '');
  const [animateur, setAnimateur] = useState(episode?.animateur ?? '');
  const [coAnimateur, setCoAnimateur] = useState(episode?.co_animateur ?? '');
  const [producer, setProducer] = useState(episode?.producer ?? '');
  const [director, setDirector] = useState(episode?.director ?? '');
  const [productionYear, setProductionYear] = useState(episode?.production_year ?? new Date().getFullYear());
  const [notes, setNotes] = useState(episode?.notes ?? '');
  const [segments, setSegments] = useState(episode?.segments ?? []);
  const [guests, setGuests] = useState(episode?.guests ?? []);
  const [sponsors, setSponsors] = useState(episode?.sponsors ?? []);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [errors, setErrors] = useState({});
  const [prefilled, setPrefilled] = useState(false);
  const [showFrames, setShowFrames] = useState(false);

  useEffect(() => {
    if (!isCreating || prefilled) return;
    let cancelled = false;
    scheduleService.getLatestEpisode(showId).then((latest) => {
      if (cancelled || !latest) return;
      setAnimateur(latest.animateur || '');
      setCoAnimateur(latest.co_animateur || '');
      setProducer(latest.producer || '');
      setDirector(latest.director || '');
      setProductionYear(latest.production_year || new Date().getFullYear());
      setSeasonNumber(latest.season_number || 1);
      setEpisodeNumber((latest.episode_number || 0) + 1);
      setPrefilled(true);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isCreating, showId, prefilled]);

  const totalDuration = computeTotalDuration(segments);
  const computedStartTimes = computeStartTimes(segments);
  const endsWithEnd = hasEndSegment(segments);

  const tabs = [
    { id: 'info', label: 'Infos' },
    { id: 'cuesheet', label: 'Cue Sheet' },
    { id: 'guests', label: 'Invités' },
    { id: 'notes', label: 'Notes' },
  ];

  const tabOrder = tabs.map((t) => t.id);

  const validateInfo = () => {
    const e = {};
    if (!seasonNumber || seasonNumber < 1) e.season = 'Saison requise';
    if (!episodeNumber || episodeNumber < 1) e.episode = 'Numéro d\'épisode requis';
    if (!title.trim()) e.title = 'Titre requis';
    return e;
  };

  const validateCuesheet = () => {
    const e = {};
    if (segments.length === 0) e.segments = 'Ajoutez au moins un segment';
    if (!hasEndSegment(segments)) e.end = 'Un segment de fin (Pause/Black avec "FIN") est requis';
    return e;
  };

  const canProceedFrom = (tab) => {
    if (tab === 'info') return Object.keys(validateInfo()).length === 0;
    if (tab === 'cuesheet') return Object.keys(validateCuesheet()).length === 0;
    return true;
  };

  const handleTabClick = (tabId) => {
    if (!isCreating) {
      setActiveTab(tabId);
      return;
    }
    const targetIdx = tabOrder.indexOf(tabId);
    const currentIdx = tabOrder.indexOf(activeTab);
    if (targetIdx <= currentIdx) {
      setActiveTab(tabId);
      return;
    }
    for (let i = currentIdx; i < targetIdx; i++) {
      if (!canProceedFrom(tabOrder[i])) {
        setErrors(tabOrder[i] === 'info' ? validateInfo() : validateCuesheet());
        return;
      }
    }
    setErrors({});
    setActiveTab(tabId);
  };

  const handleNext = () => {
    const currentIdx = tabOrder.indexOf(activeTab);
    if (currentIdx >= tabOrder.length - 1) return;

    if (!canProceedFrom(activeTab)) {
      setErrors(activeTab === 'info' ? validateInfo() : validateCuesheet());
      return;
    }
    setErrors({});
    setActiveTab(tabOrder[currentIdx + 1]);
  };

  const handlePrev = () => {
    const currentIdx = tabOrder.indexOf(activeTab);
    if (currentIdx <= 0) return;
    setErrors({});
    setActiveTab(tabOrder[currentIdx - 1]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const infoErrors = validateInfo();
    if (Object.keys(infoErrors).length > 0) {
      setErrors(infoErrors);
      setActiveTab('info');
      return;
    }
    const csErrors = validateCuesheet();
    if (Object.keys(csErrors).length > 0) {
      setErrors(csErrors);
      setActiveTab('cuesheet');
      return;
    }
    setSaving(true);
    try {
      const finalStartTimes = computeStartTimes(segments);
      await onSave({
        season_number: seasonNumber,
        episode_number: episodeNumber,
        title,
        animateur,
        co_animateur: coAnimateur,
        producer,
        director,
        production_year: productionYear || null,
        total_duration: totalDuration || null,
        notes: notes || null,
        segments: segments.map((s, i) => ({ ...s, start_time: finalStartTimes[i], sort_order: i })),
        guests: guests.map((g, i) => ({ ...g, sort_order: i })),
        sponsors: sponsors.map((s, i) => ({ ...s, sort_order: i })),
      });
    } catch {
    } finally {
      setSaving(false);
    }
  };

  // --- Segment helpers ---
  const addSegment = () => {
    setSegments([...segments, {
      bloc_number: segments.length > 0 ? segments[segments.length - 1].bloc_number || 1 : 1,
      segment_label: '',
      start_time: '0:00:00',
      description: '',
      duration: '0:00:00',
      segment_type: 'content',
    }]);
  };

  const addSponsorSegment = () => {
    setSegments([...segments, {
      bloc_number: segments.length > 0 ? segments[segments.length - 1].bloc_number || 1 : 1,
      segment_label: '',
      start_time: '0:00:00',
      description: '',
      duration: '0:00:00',
      segment_type: 'sponsor',
    }]);
  };

  const addPauseSegment = () => {
    const lastSeg = segments[segments.length - 1];
    setSegments([...segments, {
      bloc_number: lastSeg?.bloc_number || 1,
      segment_label: '',
      start_time: '0:00:00',
      description: 'PAUSE +1sec de black',
      duration: '0:01:00',
      segment_type: 'black',
    }]);
  };

  const addEndSegment = () => {
    const lastSeg = segments[segments.length - 1];
    setSegments([...segments, {
      bloc_number: lastSeg?.bloc_number || 1,
      segment_label: '',
      start_time: '0:00:00',
      description: 'FIN +1sec de black',
      duration: '0:01:00',
      segment_type: 'black',
    }]);
  };

  const updateSegment = (index, field, value) => {
    const updated = [...segments];
    updated[index] = { ...updated[index], [field]: value };
    setSegments(updated);
  };

  const removeSegment = (index) => {
    setSegments(segments.filter((_, i) => i !== index));
  };

  const moveSegment = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= segments.length) return;
    const updated = [...segments];
    const [moved] = updated.splice(fromIdx, 1);
    updated.splice(toIdx, 0, moved);
    setSegments(updated);
  };

  // --- Guest helpers ---
  const addGuest = () => {
    setGuests([...guests, { name: '', title: '' }]);
  };

  const updateGuest = (index, field, value) => {
    const updated = [...guests];
    updated[index] = { ...updated[index], [field]: value };
    setGuests(updated);
  };

  const removeGuest = (index) => {
    setGuests(guests.filter((_, i) => i !== index));
  };

  // --- Sponsor helpers ---
  const addSponsor = () => {
    setSponsors([...sponsors, { name: '', details: '' }]);
  };

  const updateSponsor = (index, field, value) => {
    const updated = [...sponsors];
    updated[index] = { ...updated[index], [field]: value };
    setSponsors(updated);
  };

  const removeSponsor = (index) => {
    setSponsors(sponsors.filter((_, i) => i !== index));
  };

  const currentTabIdx = tabOrder.indexOf(activeTab);
  const isLastTab = currentTabIdx === tabOrder.length - 1;
  const isFirstTab = currentTabIdx === 0;

  return (
    <form onSubmit={handleSubmit} onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') e.preventDefault(); }} className="episode-form">
      {/* Tabs / Wizard Steps */}
      <div className={`episode-tabs ${isCreating ? 'wizard-mode' : ''}`}>
        {tabs.map((tab, idx) => {
          const isAccessible = !isCreating || idx <= currentTabIdx;
          const isCompleted = isCreating && idx < currentTabIdx;
          return (
            <button
              key={tab.id}
              type="button"
              className={[
                'episode-tab',
                activeTab === tab.id ? 'active' : '',
                isCreating && !isAccessible ? 'locked' : '',
                isCompleted ? 'completed' : '',
              ].join(' ')}
              onClick={() => handleTabClick(tab.id)}
              disabled={isCreating && !isAccessible}
            >
              {isCreating && (
                <span className="step-number">{isCompleted ? '✓' : idx + 1}</span>
              )}
              {tab.label}
              {tab.id === 'cuesheet' && segments.length > 0 && (
                <span className="tab-badge">{segments.length}</span>
              )}
              {tab.id === 'guests' && guests.length > 0 && (
                <span className="tab-badge">{guests.length}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Error banner */}
      {Object.keys(errors).length > 0 && (
        <div className="form-errors">
          {Object.values(errors).map((msg, i) => (
            <div key={i} className="form-error-msg">{msg}</div>
          ))}
        </div>
      )}

      {/* ========== TAB: INFOS + SPONSORS ========== */}
      {activeTab === 'info' && (
        <div className="episode-tab-content">
          <div className="form-row">
            <div className="form-group">
              <label>Saison *</label>
              <input type="number" min="1" value={seasonNumber} onChange={(e) => setSeasonNumber(parseInt(e.target.value, 10) || 1)} required />
            </div>
            <div className="form-group">
              <label>Épisode *</label>
              <input type="number" min="1" value={episodeNumber} onChange={(e) => setEpisodeNumber(parseInt(e.target.value, 10) || 1)} required />
            </div>
            <div className="form-group">
              <label>Année de production</label>
              <input type="number" min="1900" max="2099" value={productionYear} onChange={(e) => setProductionYear(parseInt(e.target.value, 10) || null)} />
            </div>
          </div>
          <div className="form-group">
            <label>Titre de l'épisode *</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="ex: Gérer ses finances en couple ou séparément?" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Animateur/trice</label>
              <input type="text" value={animateur} onChange={(e) => setAnimateur(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Co-animateur/trice</label>
              <input type="text" value={coAnimateur} onChange={(e) => setCoAnimateur(e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Producteur</label>
              <input type="text" value={producer} onChange={(e) => setProducer(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Réalisateur</label>
              <input type="text" value={director} onChange={(e) => setDirector(e.target.value)} />
            </div>
          </div>

          {show?.description && (
            <div className="form-group">
              <label>Description de la série (émission)</label>
              <div className="series-desc-preview">{show.description}</div>
            </div>
          )}

          {/* Sponsors inline on info tab */}
          <div className="info-section-divider">
            <h4>Commanditaires</h4>
            <button type="button" className="btn btn-secondary btn-sm" onClick={addSponsor}>+ Commanditaire</button>
          </div>
          {sponsors.length === 0 ? (
            <p className="empty-state-sm">Aucun commanditaire.</p>
          ) : (
            <div className="sponsors-inline-list">
              {sponsors.map((sponsor, idx) => (
                <div key={idx} className="sponsor-inline-card">
                  <div className="form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <input type="text" value={sponsor.name || ''} onChange={(e) => updateSponsor(idx, 'name', e.target.value)} placeholder="Nom du commanditaire" />
                    </div>
                    <div className="form-group" style={{ flex: 2 }}>
                      <input type="text" value={sponsor.details || ''} onChange={(e) => updateSponsor(idx, 'details', e.target.value)} placeholder="Détails du placement" />
                    </div>
                    <button type="button" className="btn-remove" onClick={() => removeSponsor(idx)} title="Supprimer">×</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========== TAB: CUE SHEET ========== */}
      {activeTab === 'cuesheet' && (
        <div className="episode-tab-content">
          <div className="cuesheet-header">
            <h3>Feuille de route (Cue Sheet)</h3>
            <div className="cuesheet-actions">
              <button type="button" className="btn btn-secondary btn-sm" onClick={addSegment}>+ Segment</button>
              <button type="button" className="btn btn-sm btn-sponsor-add" onClick={addSponsorSegment}>+ Commanditaire</button>
              <button type="button" className="btn btn-sm btn-black" onClick={addPauseSegment}>+ Pause</button>
              <button type="button" className="btn btn-sm btn-end" onClick={addEndSegment}>+ Fin</button>
            </div>
          </div>

          {segments.length === 0 ? (
            <p className="empty-state">Aucun segment. Cliquez sur un bouton ci-haut pour commencer.</p>
          ) : (
            <>
              <div className="cuesheet-toggle-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={showFrames}
                    onChange={(e) => setShowFrames(e.target.checked)}
                  />
                  Afficher les images (frames)
                </label>
              </div>
              <div className="cuesheet-table-wrapper">
                <table className="cuesheet-table">
                  <thead>
                    <tr>
                      <th className="col-move"></th>
                      <th className="col-bloc">Bloc</th>
                      <th className="col-label">Segment</th>
                      <th className="col-time">Temps</th>
                      <th className="col-desc">Description</th>
                      <th className="col-dur">Durée</th>
                      <th className="col-type">Type</th>
                      <th className="col-action"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {segments.map((seg, idx) => {
                      const isBlackOrPause = seg.segment_type === 'black' || seg.segment_type === 'pause';
                      const isSponsor = seg.segment_type === 'sponsor';
                      const rowClass = isBlackOrPause ? 'seg-black' : isSponsor ? 'seg-sponsor' : '';
                      const startTimeStr = computedStartTimes[idx] || '0:00:00';
                      const { mmss: startMmss, frames: startFrames } = splitTime(startTimeStr);
                      const { mmss: durMmss, frames: durFrames } = splitTime(seg.duration);

                      return (
                        <tr key={idx} className={`cuesheet-row ${rowClass}`}>
                          <td className="move-cell">
                            <div className="move-buttons">
                              <button
                                type="button"
                                className="btn-move"
                                onClick={() => moveSegment(idx, idx - 1)}
                                disabled={idx === 0}
                                title="Monter"
                              >▲</button>
                              <button
                                type="button"
                                className="btn-move"
                                onClick={() => moveSegment(idx, idx + 1)}
                                disabled={idx === segments.length - 1}
                                title="Descendre"
                              >▼</button>
                            </div>
                          </td>
                          <td>
                            <input
                              type="number"
                              min="1"
                              className="input-mini"
                              value={seg.bloc_number || ''}
                              onChange={(e) => updateSegment(idx, 'bloc_number', parseInt(e.target.value, 10) || null)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="input-sm"
                              value={seg.segment_label || ''}
                              onChange={(e) => updateSegment(idx, 'segment_label', e.target.value)}
                              placeholder="Seg. 1"
                            />
                          </td>
                          <td>
                            <span className="time-display">
                              {startMmss}{showFrames ? `:${String(startFrames).padStart(2, '0')}` : ''}
                            </span>
                          </td>
                          <td>
                            {isSponsor && sponsors.length > 0 ? (
                              <select
                                className="input-desc"
                                value={seg.description || ''}
                                onChange={(e) => updateSegment(idx, 'description', e.target.value)}
                              >
                                <option value="">— Choisir un commanditaire —</option>
                                {sponsors.map((sp, spIdx) => (
                                  <option key={spIdx} value={sp.name}>{sp.name}{sp.details ? ` — ${sp.details}` : ''}</option>
                                ))}
                              </select>
                            ) : isBlackOrPause ? (
                              <input
                                type="text"
                                className="input-desc input-black"
                                value={seg.description || ''}
                                onChange={(e) => updateSegment(idx, 'description', e.target.value)}
                              />
                            ) : (
                              <textarea
                                className="input-desc"
                                value={seg.description || ''}
                                onChange={(e) => updateSegment(idx, 'description', e.target.value)}
                                rows={2}
                              />
                            )}
                          </td>
                          <td>
                            <div className="duration-inputs">
                              <input
                                type="text"
                                className="input-time"
                                value={durMmss}
                                onChange={(e) => updateSegment(idx, 'duration', joinTime(e.target.value, durFrames))}
                                placeholder="0:00"
                              />
                              {showFrames && (
                                <input
                                  type="number"
                                  className="input-frames"
                                  min="0"
                                  max="29"
                                  value={durFrames}
                                  onChange={(e) => updateSegment(idx, 'duration', joinTime(durMmss, e.target.value))}
                                  title="Images (0-29)"
                                />
                              )}
                            </div>
                          </td>
                          <td>
                            <select
                              className="input-type"
                              value={seg.segment_type || 'content'}
                              onChange={(e) => updateSegment(idx, 'segment_type', e.target.value)}
                            >
                              {SEGMENT_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <button type="button" className="btn-remove" onClick={() => removeSegment(idx)} title="Supprimer">×</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {segments.length > 0 && (
            <div className="cuesheet-footer">
              <span className="cuesheet-total">
                Durée totale: <strong>{showFrames ? totalDuration : splitTime(totalDuration).mmss}</strong>
              </span>
              <span className="cuesheet-pauses">
                Pauses: <strong>{segments.filter((s) => s.segment_type === 'pause' || (s.segment_type === 'black' && !/fin/i.test(s.description || ''))).length}</strong>
              </span>
              {!endsWithEnd && (
                <span className="cuesheet-warning">⚠ Ajoutez un segment de fin</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* ========== TAB: GUESTS ========== */}
      {activeTab === 'guests' && (
        <div className="episode-tab-content">
          <div className="cuesheet-header">
            <h3>Invités</h3>
            <button type="button" className="btn btn-secondary btn-sm" onClick={addGuest}>+ Invité</button>
          </div>
          {guests.length === 0 ? (
            <p className="empty-state">Aucun invité. Cette section est optionnelle.</p>
          ) : (
            <div className="guests-list">
              {guests.map((guest, idx) => (
                <div key={idx} className="guest-card">
                  <div className="form-group">
                    <label>Nom de l'invité {idx + 1}</label>
                    <input type="text" value={guest.name || ''} onChange={(e) => updateGuest(idx, 'name', e.target.value)} placeholder="Nom complet" />
                  </div>
                  <div className="form-group">
                    <label>Titre / Fonction</label>
                    <textarea value={guest.title || ''} onChange={(e) => updateGuest(idx, 'title', e.target.value)} rows={2} placeholder="Ex: Présidente du cabinet..." />
                  </div>
                  <button type="button" className="btn-remove guest-remove" onClick={() => removeGuest(idx)} title="Supprimer">×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========== TAB: NOTES ========== */}
      {activeTab === 'notes' && (
        <div className="episode-tab-content">
          <div className="form-group">
            <label>Notes spéciales</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={6}
              placeholder="ex: PRODUCTION 2025, remplacements, remarques techniques..."
            />
          </div>
        </div>
      )}

      {/* ========== FOOTER: Navigation + Save ========== */}
      <div className="modal-actions">
        {isCreating ? (
          <>
            <button type="button" className="btn btn-secondary" onClick={onCancel}>Annuler</button>
            <div className="wizard-nav">
              {!isFirstTab && (
                <button type="button" className="btn btn-secondary" onClick={handlePrev}>← Précédent</button>
              )}
              {!isLastTab ? (
                <button key="wizard-next" type="button" className="btn btn-primary" onClick={handleNext}>Suivant →</button>
              ) : (
                <button key="wizard-submit" type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Enregistrement...' : 'Créer l\'épisode'}
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-secondary" onClick={onCancel}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </>
        )}
      </div>
    </form>
  );
}
